import { z } from "zod";

// Stable lookup keys for the two admin-uploadable assets. Defined here (a
// dependency-light, zod-only module) so both client and server can import them
// without the client bundle pulling in the DB layer from `@/api/branding`.
export const ASSET_SLUGS = {
  homepageLogo: "homepage_logo",
  userGuide: "user_guide",
} as const;

export type AssetSlug = (typeof ASSET_SLUGS)[keyof typeof ASSET_SLUGS];

export function isAssetSlug(value: string): value is AssetSlug {
  return value === ASSET_SLUGS.homepageLogo || value === ASSET_SLUGS.userGuide;
}

// Per-asset metadata returned by GET /api/branding. `data` (the file bytes) is
// never sent in this payload — the bytes are fetched from the dedicated
// content route only when actually rendered/downloaded.
export const assetMetaSchema = z.object({
  fileName: z.string(),
  contentType: z.string(),
  // serialized as an ISO string over the wire
  updatedOn: z.string(),
});
export type AssetMeta = z.infer<typeof assetMetaSchema>;

export const brandingSchema = z.object({
  logo: assetMetaSchema.nullable(),
  userGuide: assetMetaSchema.nullable(),
});
export type Branding = z.infer<typeof brandingSchema>;

// Per-slug upload constraints, enforced server-side. Logos are small images;
// the user guide is a PDF that can be a few MB.
export const LOGO_MAX_BYTES = 1_000_000; // 1 MB
export const GUIDE_MAX_BYTES = 10_000_000; // 10 MB

export const LOGO_CONTENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
] as const;
export const GUIDE_CONTENT_TYPES = ["application/pdf"] as const;
